#!/usr/bin/env node

import fs from 'fs/promises';
import path from 'path';

import { CoderuleClients } from '@coderule/clients';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';

import { resolveConfig } from './config/Configurator';
import { bootstrap } from './service/Bootstrap';
import { runInitialSyncPipeline } from './service/InitialSync';
import { ServiceRunner } from './service/ServiceLoops';
import { createMcpServer } from './mcp/server';

const ENV_FLAG_MAP: Record<string, string> = {
  'data-dir': 'CODERULE_DATA_DIR',
  'auth-url': 'CODERULE_AUTH_URL',
  'sync-url': 'CODERULE_SYNC_URL',
  'ast-url': 'CODERULE_AST_URL',
  'retrieval-url': 'CODERULE_RETRIEVAL_URL',
  'retrieval-formatter': 'CODERULE_RETRIEVAL_FORMATTER',
  'http-timeout': 'CODERULE_HTTP_TIMEOUT',
  'snapshot-debounce': 'CODERULE_SNAPSHOT_DEBOUNCE_MS',
  'heartbeat-interval': 'CODERULE_HEARTBEAT_INTERVAL_MS',
  'heartbeat-check': 'CODERULE_HEARTBEAT_CHECK_INTERVAL_MS',
  'queue-poll': 'CODERULE_QUEUE_POLL_INTERVAL_MS',
  'hash-batch': 'CODERULE_HASH_BATCH_SIZE',
  'hash-lease': 'CODERULE_HASH_LEASE_MS',
  'max-snapshot-attempts': 'CODERULE_MAX_SNAPSHOT_ATTEMPTS',
  'upload-chunk-size': 'CODERULE_UPLOAD_CHUNK_SIZE',
  'max-wait-time': 'CODERULE_MAX_WAIT_TIME',
};

interface CliOptions {
  token: string;
  rootPath?: string;
  clean: boolean;
  exit: boolean;
  inlineHasher: boolean;
  dumpBush?: string;
  dumpSkill: boolean;
  env: Record<string, string>;
}

function printUsage(): void {
  console.log(`Usage: coderule-mcp-server [token] [options]\n`);
  console.log('Options:');
  console.log('  --token <token>              Override CODERULE_TOKEN');
  console.log(
    '  --clean, --reindex           Remove existing local state before running',
  );
  console.log(
    '  --exit                       Exit after reindex (use with --reindex)',
  );
  console.log(
    '  --inline-hasher              Force inline hashing (debug only)',
  );
  console.log('  --root <path>                Override CODERULE_ROOT');
  console.log('  --data-dir <path>            Override CODERULE_DATA_DIR');
  console.log('  --auth-url <url>             Override CODERULE_AUTH_URL');
  console.log('  --sync-url <url>             Override CODERULE_SYNC_URL');
  console.log('  --ast-url <url>              Override CODERULE_AST_URL');
  console.log('  --retrieval-url <url>        Override CODERULE_RETRIEVAL_URL');
  console.log(
    '  --retrieval-formatter <val>  Override CODERULE_RETRIEVAL_FORMATTER (standard | compact)',
  );
  console.log('  --http-timeout <ms>          Override CODERULE_HTTP_TIMEOUT');
  console.log(
    '  --snapshot-debounce <ms>     Override CODERULE_SNAPSHOT_DEBOUNCE_MS',
  );
  console.log(
    '  --heartbeat-interval <ms>    Override CODERULE_HEARTBEAT_INTERVAL_MS',
  );
  console.log(
    '  --heartbeat-check <ms>       Override CODERULE_HEARTBEAT_CHECK_INTERVAL_MS',
  );
  console.log(
    '  --queue-poll <ms>            Override CODERULE_QUEUE_POLL_INTERVAL_MS',
  );
  console.log(
    '  --hash-batch <n>             Override CODERULE_HASH_BATCH_SIZE',
  );
  console.log('  --hash-lease <ms>            Override CODERULE_HASH_LEASE_MS');
  console.log(
    '  --max-snapshot-attempts <n>  Override CODERULE_MAX_SNAPSHOT_ATTEMPTS',
  );
  console.log(
    '  --upload-chunk-size <n>      Override CODERULE_UPLOAD_CHUNK_SIZE (default 1)',
  );
  console.log(
    '  --max-wait-time <sec>        Override CODERULE_MAX_WAIT_TIME (default 50s)',
  );
  console.log(
    '  --dump-bush <path>           Download HDF5 bush file (must end with .h5)',
  );
  console.log(
    '  --dump-skill                 Print HDF5 bush reader skill (format docs)',
  );
  console.log(
    '  KEY=value                    Set arbitrary environment variable',
  );
  console.log('  -h, --help                   Show this help message');
}

function parseArgs(argv: string[]): CliOptions | null {
  let token = process.env.CODERULE_TOKEN;
  let rootPath: string | undefined;
  let clean = false;
  let exit = false;
  let inlineHasher = false;
  let dumpBush: string | undefined;
  let dumpSkill = false;
  const env: Record<string, string> = {};

  const args = [...argv];
  while (args.length > 0) {
    const arg = args.shift()!;
    if (arg === '--help' || arg === '-h') {
      printUsage();
      return null;
    }

    if (arg === '--clean' || arg === '--reindex') {
      clean = true;
      continue;
    }
    if (arg === '--exit') {
      exit = true;
      continue;
    }
    if (arg === '--inline-hasher') {
      inlineHasher = true;
      continue;
    }
    if (arg === '--dump-bush') {
      const value = args.shift();
      if (!value) {
        throw new Error('Missing value for --dump-bush');
      }
      if (!value.endsWith('.h5')) {
        throw new Error('--dump-bush path must end with .h5');
      }
      dumpBush = value;
      exit = true;
      clean = true;
      continue;
    }
    if (arg === '--dump-skill') {
      dumpSkill = true;
      exit = true;
      clean = true;
      continue;
    }

    if (arg === '--token') {
      const value = args.shift();
      if (!value) {
        throw new Error('Missing value for --token');
      }
      token = value;
      continue;
    }
    if (arg.startsWith('--token=')) {
      token = arg.slice('--token='.length);
      continue;
    }

    // Handle --root separately (not as env var)
    if (arg === '--root') {
      const value = args.shift();
      if (!value) {
        throw new Error('Missing value for --root');
      }
      rootPath = value;
      continue;
    }

    if (arg.startsWith('--')) {
      const flag = arg.slice(2);
      const envKey = ENV_FLAG_MAP[flag];
      if (!envKey) {
        throw new Error(`Unknown option: ${arg}`);
      }
      const value = args.shift();
      if (!value) {
        throw new Error(`Option ${arg} requires a value`);
      }
      env[envKey] = value;
      continue;
    }

    if (arg.includes('=')) {
      const [key, value] = arg.split('=', 2);
      if (!key || value === undefined) {
        throw new Error(`Invalid KEY=value argument: ${arg}`);
      }
      env[key] = value;
      continue;
    }

    if (!token) {
      token = arg;
      continue;
    }

    throw new Error(`Unexpected argument: ${arg}`);
  }

  if (!token) {
    throw new Error(
      'Missing token. Provide via argument or CODERULE_TOKEN environment variable.',
    );
  }

  return {
    token,
    rootPath,
    clean,
    exit,
    inlineHasher,
    dumpBush,
    dumpSkill,
    env,
  };
}

async function ensureClean(
  configToken: string,
  rootPath?: string,
): Promise<void> {
  const config = await resolveConfig({ token: configToken, rootPath });
  const targets = [
    config.dbPath,
    `${config.dbPath}-shm`,
    `${config.dbPath}-wal`,
  ];
  await Promise.all(targets.map((target) => fs.rm(target, { force: true })));
  await fs
    .rm(path.join(config.dataDir, 'watch', `${config.rootId}.sqlite-shm`), {
      force: true,
    })
    .catch(() => {});
  const dir = path.dirname(config.dbPath);
  await fs.mkdir(dir, { recursive: true });
  console.log(`Removed scanner database at ${config.dbPath}`);
}

function awaitShutdownSignals(): Promise<NodeJS.Signals> {
  return new Promise((resolve) => {
    const signals: NodeJS.Signals[] = ['SIGINT', 'SIGTERM'];
    const handler = (signal: NodeJS.Signals) => {
      for (const sig of signals) {
        process.off(sig, handler);
      }
      resolve(signal);
    };
    for (const sig of signals) {
      process.on(sig, handler);
    }
  });
}

async function main(): Promise<void> {
  try {
    const options = parseArgs(process.argv.slice(2));
    if (!options) {
      return;
    }

    process.env.CODERULE_TOKEN = options.token;
    if (options.inlineHasher) {
      process.env.CODERULE_HASHER_INLINE = '1';
    }
    for (const [key, value] of Object.entries(options.env)) {
      process.env[key] = value;
    }

    if (options.clean) {
      await ensureClean(options.token, options.rootPath);
    }

    const runtime = await bootstrap({
      token: options.token,
      rootPath: options.rootPath,
    });
    const runner = new ServiceRunner(runtime);

    // Exit mode: run initial sync and exit without starting MCP server
    if (options.exit && options.clean) {
      try {
        const initial = await runInitialSyncPipeline(runtime, {
          blockUntilReady: true,
        });
        runtime.logger.info(
          {
            snapshotHash: initial.snapshotHash,
            filesCount: initial.filesCount,
          },
          'Reindex completed; exiting',
        );

        if (options.dumpBush || options.dumpSkill) {
          const clients = new CoderuleClients({ token: options.token });
          try {
            // Trigger auth to discover sync server_url
            await clients.sync.checkSnapshotStatus(initial.snapshotHash);

            if (options.dumpBush) {
              const buf = await clients.sync.downloadSnapshotBush(
                initial.snapshotHash,
              );
              await fs.mkdir(path.dirname(options.dumpBush), {
                recursive: true,
              });
              await fs.writeFile(options.dumpBush, Buffer.from(buf));
              runtime.logger.info(
                { filePath: options.dumpBush, bytes: buf.byteLength },
                'HDF5 bush file saved',
              );
            }
            if (options.dumpSkill) {
              const skill = await clients.sync.getHdf5BushReaderSkill();
              console.log(skill);
            }
          } finally {
            clients.close();
          }
        }
      } finally {
        await runtime.hasher.close();
        runtime.clients.close();
        runtime.db.close();
      }
      return;
    }

    try {
      await runner.prepareWatcher(true);

      // Start MCP server immediately so tools are available while syncing
      const server = createMcpServer({ runtime, runner });
      const transport = new StdioServerTransport();
      await server.connect(transport);
      runtime.logger.info('MCP server connected via stdio');

      let initialCreatedAt: number | undefined;
      try {
        const initial = await runInitialSyncPipeline(runtime, {
          blockUntilReady: false,
        });
        runtime.logger.info(
          {
            snapshotHash: initial.snapshotHash,
            filesCount: initial.filesCount,
          },
          'Initial sync completed; entering continuous mode',
        );
        initialCreatedAt = initial.createdAt;
      } catch (error: any) {
        runtime.logger.warn(
          { err: error },
          'Initial sync failed; enqueuing snapshot job and continuing',
        );
        runtime.outbox.enqueueSnapshot(runtime.config.rootId, 0);
      }

      if (initialCreatedAt) {
        runner.recordInitialSnapshot(initialCreatedAt);
      }
      await runner.startLoops();
      await runner.enableWatcherProcessing();

      const signal = await awaitShutdownSignals();
      runtime.logger.info({ signal }, 'Shutdown signal received');
      if (typeof transport.close === 'function') {
        await transport.close();
      }
    } finally {
      await runner.stop();
    }
  } catch (error) {
    console.error('MCP server failed:', error);
    process.exitCode = 1;
  }
}

void main();
